import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { useUpdateLead, type Lead } from "@/hooks/useLeads";

const QUICK_REASONS = [
  "Preço",
  "Sem resposta",
  "Fechou com outro",
  "Fora do escopo",
  "Adiou o projeto",
];

interface Props {
  lead: Pick<Lead, "id" | "name"> | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onDone?: () => void;
}

/** "Marcar como perdido" with a required reason — shared by the funnel card menu and the lead page. */
export function MarkLostDialog({ lead, open, onOpenChange, onDone }: Props) {
  const [reason, setReason] = useState("");
  const updateLead = useUpdateLead();

  const submit = () => {
    if (!lead || !reason.trim()) return;
    updateLead.mutate(
      {
        id: lead.id,
        data: { status: "lost", lost_reason: reason.trim(), lost_at: new Date().toISOString() },
      },
      {
        onSuccess: () => {
          setReason("");
          onOpenChange(false);
          onDone?.();
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setReason("");
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Marcar "{lead?.name}" como perdido?</DialogTitle>
          <DialogDescription>
            O lead sai do funil e vai para a lista de perdidos. Você pode reabrir depois.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <span id="lost-reason-label" className="text-sm font-medium leading-none">
            Motivo
          </span>
          <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby="lost-reason-label">
            {QUICK_REASONS.map((r) => (
              <Button
                key={r}
                type="button"
                size="sm"
                variant={reason === r ? "default" : "outline"}
                className="h-7 text-xs"
                onClick={() => setReason(r)}
              >
                {r}
              </Button>
            ))}
          </div>
          <Textarea
            aria-labelledby="lost-reason-label"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: achou caro, vai retomar em 2027…"
            rows={2}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            className="bg-bad text-bad-foreground hover:bg-bad/90"
            onClick={submit}
            disabled={!reason.trim() || updateLead.isPending}
          >
            {updateLead.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Marcar como perdido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
